import { Injectable, isDevMode } from '@angular/core';
import { v4 as uuidv4 } from 'uuid';
import { StorageService } from './storage.service';
import { forkJoin, Observable, Subject } from 'rxjs';
import {
  TID_REDIRECT_URL,
  TidAuthParams,
  TidRegistrationData,
  TidUiParams,
} from '../containers/tid/tid.interface';
import { ActionsService } from '../api/actions.service';
import { take } from 'rxjs/operators';
import { environment } from '../../environments/environment';

declare var TidSDK: any;

/**
 * Tinkoff ID service
 * Сервис регистрации клиентов через Tinkoff ID
 */
@Injectable()
export class TidService {
  private RESPONSE_TYPE: string = 'code';
  private COOKIE_STATE_NAME: string = 'tid_state';
  private APPLICATION_ID_VIVUS: string = 'tid_vivus';
  private APPLICATION_ID_SMSFINANCE: string = 'tid_smsfinance';

  private BRAND = '';

  private REDIRECT_URL = TID_REDIRECT_URL;

  private STORAGE_REG_DATA_FIELD: string = 'tid_reg_data';
  private STORAGE_ERROR_FIELD: string = 'tid_error';

  public $activeRegistrationData: Subject<TidRegistrationData> =
    new Subject<TidRegistrationData>();

  private uiParams: TidUiParams = {
    container: '#tid-button-container',
    size: 'm',
    color: 'primary',
    text: 'Т-Банк',
    target: '_self',
  };

  constructor(
    private _storage: StorageService,
    private _actionService: ActionsService
  ) {
    this.BRAND = environment.brand;
  }

  /**
   * Получение активной сессии и данных шага, который нужно
   * дозаполнить
   */
  public get activeRegistrationProcess(): Observable<TidRegistrationData> {
    return this.$activeRegistrationData;
  }

  public setRegistrationSession(token: string, state: string, error?: string) {
    if (error) {
      this.saveError(error);
    }

    if (token && state) {
      if (state === this.getState()) {
        forkJoin({
          client: this._actionService.tidClientRegistrationData(token, state),
          passport: this._actionService.tidPassportRegistrationData(
            token,
            state
          ),
          addresses: this._actionService.tidAddressRegistrationData(
            token,
            state
          ),
        })
          .pipe(take(1))
          .subscribe({
            next: ({ client, passport, addresses }) => {
              const tidRegistrationData: TidRegistrationData = {
                client,
                passport,
                addresses,
              };
              this.saveRegistrationDataToStorage(tidRegistrationData);
            },
            error: (error) => {
              this.saveError(error);
            },
          });
      } else {
        this.saveError('ERROR');
      }
    }
  }

  private saveError(error: string) {
    this._storage.set(this.STORAGE_ERROR_FIELD, error);
  }

  private isError(): boolean {
    return this._storage.check(this.STORAGE_ERROR_FIELD);
  }

  private passWithError() {
    this.$activeRegistrationData.error(this.STORAGE_ERROR_FIELD);
    this.clearSession();
  }

  /**
   * Заполнение данных либо из storage, либо из запроса
   * @private
   */
  public fillRegistrationData() {
    const isError = this.isError();

    if (isError) {
      this.passWithError();
      return;
    }

    const registrationData = this.getRegistrationDataFromStorage();
    this.$activeRegistrationData.next(registrationData);
  }

  /**
   * Инициализация кнопки для перехода на логин в TID
   * @param brand
   */
  public initiateDefaultRegistrationButton(brand: string) {
    const authParams = this.generateAuthParams();

    console.log(authParams);

    const tid = new TidSDK(authParams);
    tid.addButton(this.uiParams);
  }

  /**
   * Генератор параметров авторизации
   * @private
   */
  private generateAuthParams(): TidAuthParams {
    const state = this.generateState();
    this.setState(state);

    const prepareRedirectUri = () => {
      if (isDevMode()) {
        return this.REDIRECT_URL[this.BRAND].DEV;
      }

      return window.location.href.includes('stage')
        ? this.REDIRECT_URL[this.BRAND].DEV
        : this.REDIRECT_URL[this.BRAND].PROD;
    };

    const prepareApplicationId = () =>
      environment.brand === 'VIVUS'
        ? this.APPLICATION_ID_VIVUS
        : this.APPLICATION_ID_SMSFINANCE;

    return {
      redirectUri: prepareRedirectUri(),
      responseType: this.RESPONSE_TYPE,
      clientId: prepareApplicationId(),
      state,
    };
  }

  /**
   * Сохранение данных регистрации, для предотвращения потери при
   * перезагрузке приложения
   * @param data
   * @private
   */
  private saveRegistrationDataToStorage(data: TidRegistrationData) {
    this._storage.set(this.STORAGE_REG_DATA_FIELD, JSON.stringify(data));
  }

  /**
   * Получение сохраненной сессии регистрации
   * @private
   */
  private getRegistrationDataFromStorage(): TidRegistrationData | null {
    const regData = this._storage.check(this.STORAGE_REG_DATA_FIELD);
    return regData
      ? JSON.parse(this._storage.get(this.STORAGE_REG_DATA_FIELD))
      : null;
  }

  public setState(state: string) {
    this._storage.set(this.COOKIE_STATE_NAME, state);
  }

  public getState(): string | Error {
    const state = this._storage.check(this.COOKIE_STATE_NAME);
    return state ? this._storage.get(this.COOKIE_STATE_NAME) : new Error();
  }

  public clearSession() {
    const state = this._storage.check(this.COOKIE_STATE_NAME);
    const regData = this._storage.check(this.STORAGE_REG_DATA_FIELD);
    const error = this._storage.check(this.STORAGE_ERROR_FIELD);

    state && this._storage.remove(this.COOKIE_STATE_NAME);
    regData && this._storage.remove(this.STORAGE_REG_DATA_FIELD);
    error && this._storage.remove(this.STORAGE_ERROR_FIELD);
  }

  private generateState(): string {
    return uuidv4();
  }
}
